// API Client for Librario

import { PreviewItem, Config } from './types';
import { debugConsole } from './debug';

/**
 * Fetch catalog preview (first 10 items)
 * Note: This may not be implemented in backend yet
 */
export async function fetchCatalogPreview(
  _config: Config, // Unused until backend implements preview endpoint
  catalogId: string
): Promise<PreviewItem[]> {
  debugConsole.log(`Fetching preview for catalog: ${catalogId}`);

  try {
    // TODO: Implement when backend supports preview endpoint
    // For now, return mock data or empty array
    debugConsole.log('Catalog preview not yet implemented in backend');
    return [];
  } catch (error) {
    debugConsole.log(`Error fetching catalog preview: ${error}`);
    return [];
  }
}

/**
 * Validate configuration endpoint (if available)
 */
export async function validateConfig(config: Config): Promise<boolean> {
  debugConsole.log('Validating configuration');

  try {
    // Basic client-side validation
    if (!config.authKey || config.authKey.trim() === '') {
      debugConsole.log('Config validation failed: No auth key');
      return false;
    }

    if (config.cacheValiditySeconds < 300 || config.cacheValiditySeconds > 86400) {
      debugConsole.log('Config validation failed: Invalid cache validity');
      return false;
    }

    if (!config.catalogs || config.catalogs.length === 0) {
      debugConsole.log('Config validation failed: No catalogs defined');
      return false;
    }

    // Ensure at least one catalog is enabled
    const hasEnabledCatalog = config.catalogs.some(c => c.enabled);
    if (!hasEnabledCatalog) {
      debugConsole.log('Config validation failed: No enabled catalogs');
      return false;
    }

    debugConsole.log('Config validation passed');
    return true;
  } catch (error) {
    debugConsole.log(`Error validating config: ${error}`);
    return false;
  }
}

/**
 * Fetch metadata from Cinemeta (for preview enrichment)
 */
export async function fetchCinemetaMeta(
  imdbId: string,
  type: string
): Promise<any | null> {
  debugConsole.log(`Fetching Cinemeta metadata for ${type}/${imdbId}`);

  try {
    const response = await fetch(
      `https://v3-cinemeta.strem.io/meta/${type}/${imdbId}.json`
    );

    if (!response.ok) {
      debugConsole.log(`Cinemeta fetch failed: HTTP ${response.status}`);
      return null;
    }

    const data = await response.json();
    debugConsole.log(`Cinemeta metadata fetched for ${imdbId}`);
    return data.meta || null;
  } catch (error) {
    debugConsole.log(`Error fetching Cinemeta metadata: ${error}`);
    return null;
  }
}

/**
 * Fetch hoster-configured cache limits
 */
export async function fetchHostedLimits(): Promise<{
  minLibraryCacheTTL: number;
  maxLibraryCacheTTL: number;
  enableLibraryCache: boolean;
} | null> {
  try {
    const response = await fetch('/api/config/limits');
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    debugConsole.log('Failed to fetch hoster limits:', String(error));
    return null;
  }
}
